/**
 * Ads-measurement permission, shared between ocesha.ai (website) and app.ocesha.ai.
 *
 * Validated shared cookie (same site, Domain=.ocesha.ai):
 *     ocesha_consent = encodeURIComponent(JSON.stringify({ v: 1, ads: true|false, ts: <unix ms> }))
 * Either site writes it when the visitor chooses; both read it, so a rejection or withdrawal
 * on one site applies on the other. A value is accepted only when it parses as JSON with
 * v === 1, a boolean `ads` and a plausible timestamp (not in the future, < 13 months old);
 * anything else counts as "no decision". URL attribution parameters (oppref, utm_*) never
 * count as permission.
 */
export type AdsConsent = "granted" | "denied" | "unknown";

const COOKIE = "ocesha_consent";
const MAX_AGE_DAYS = 395;                       // ~13 months
const EVENT = "ads-consent-changed";

const cookieDomain = (): string => {
  const h = window.location.hostname;
  return h === "ocesha.ai" || h.endsWith(".ocesha.ai") ? "; Domain=.ocesha.ai" : "";
};

const readCookie = (name: string): string | null => {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return m ? m[1] : null;
};

/** Parse + validate the shared cookie value. */
export const parseConsentCookie = (raw: string | null): AdsConsent => {
  if (!raw) return "unknown";
  try {
    const v = JSON.parse(decodeURIComponent(raw));
    if (!v || v.v !== 1 || typeof v.ads !== "boolean" || typeof v.ts !== "number") return "unknown";
    const now = Date.now();
    if (v.ts > now + 5 * 60 * 1000 || now - v.ts > MAX_AGE_DAYS * 86400000) return "unknown";
    return v.ads ? "granted" : "denied";
  } catch {
    return "unknown";
  }
};

export const getAdsConsent = (): AdsConsent => parseConsentCookie(readCookie(COOKIE));

export const setAdsConsent = (granted: boolean, source = "app_banner") => {
  const value = encodeURIComponent(JSON.stringify({ v: 1, ads: granted, ts: Date.now() }));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE}=${value}; Path=/; Max-Age=${MAX_AGE_DAYS * 86400}; SameSite=Lax${secure}${cookieDomain()}`;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { consent: granted ? "granted" : "denied", source } }));
};

export const onAdsConsentChange = (fn: (c: AdsConsent, source: string) => void) => {
  const h = (e: Event) => fn((e as CustomEvent).detail?.consent, (e as CustomEvent).detail?.source || "app");
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
};

/** Remove the pixel's first-party cookies (rejection / withdrawal). */
export const clearPixelCookies = () => {
  ["__oppref", "__obref"].forEach((n) => {
    document.cookie = `${n}=; Path=/; Max-Age=0`;
    document.cookie = `${n}=; Path=/; Max-Age=0${cookieDomain()}`;
  });
};

export const readPixelBrowserRef = (): string | null => readCookie("__obref");
